import { PropsWithChildren, useEffect } from 'react';

import { Navigate, useNavigate, useSearchParams } from 'react-router';

import { Button, Card, Divider, ElvaLogoName, Heading4, Inline, Page, Stack, Text, u } from '@ui';

import { QR_KEY } from '../../consts';
import { useConsumeQRCode, useUser } from '../../hooks';

type QRCodeScreenProps = {
    redirectPath: string;
};
/**
 * This screen is used in app only.
 */
export function QRCodeScreen({ redirectPath }: QRCodeScreenProps) {
    const { data: user, isLoading } = useUser();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const qrCode = params.get(QR_KEY);

    if (isLoading) {
        return (
            <Container>
                <Stack gap={u(2.5)}>
                    <Heading4>Checking session…</Heading4>
                    <Text color="muted">Please wait while we verify your login status.</Text>
                </Stack>
            </Container>
        );
    }

    /**
     * User is not logged in so we:
     *   1. Inform user that users need active session to consume QR codes
     *   2. We offer him a quick route back to login
     */
    if (!user) {
        return (
            <Container>
                <Stack gap={u(2.5)}>
                    <Heading4>Login required</Heading4>
                    <Text color="muted">Please login and then scan the QR code again.</Text>
                    <Button size="small" onClick={() => navigate(redirectPath)}>
                        Goto login page
                    </Button>
                </Stack>
            </Container>
        );
    }

    /**
     * If the user does not have a qr code but somehow routed to this page we redirect to base
     * and let the router decide what to show
     */
    if (!qrCode) {
        return <Navigate to="/" replace />;
    }

    return <QRCodeConsumer qrCode={qrCode} />;
}

type QRCodeConsumerProps = {
    qrCode: string;
};

function QRCodeConsumer({ qrCode }: QRCodeConsumerProps) {
    const { mutateAsync, isPending, isError } = useConsumeQRCode();
    const navigate = useNavigate();
    useEffect(() => {
        mutateAsync(qrCode);
    }, [mutateAsync, qrCode]);

    if (isPending) {
        return (
            <Container>
                <Stack gap={u(2.5)}>
                    <Heading4>Processing QR Code</Heading4>
                    <Text color="muted">Please wait while we process your QR code.</Text>
                </Stack>
            </Container>
        );
    }

    /**
     * Should we give reasons here? Request too old, offer the user a simple way to claim booth session with phone and qr code?
     * Should we offer a manual retry mechanism here?
     */
    if (isError) {
        return (
            <Container>
                <Stack gap={u(2.5)}>
                    <Heading4>QR Code Failed</Heading4>
                    <Text color="muted">We could not process this QR code.</Text>
                    <Button size="small" onClick={() => navigate('/')}>
                        Goto app
                    </Button>
                </Stack>
            </Container>
        );
    }

    return (
        <Container>
            <Stack gap={u(2.5)}>
                <Heading4>QR Code Processed</Heading4>
                <Text color="muted">Your QR code has been processed successfully.</Text>
                <Button size="small" onClick={() => navigate('/')}>
                    Goto app
                </Button>
            </Stack>
        </Container>
    );
}

const Container = ({ children }: PropsWithChildren) => {
    return (
        <Page>
            <Card>
                <Stack gap={u(2.5)}>
                    <Inline gap={u(2)} alignItems="center" justifyContent="space-between">
                        {children}
                        <ElvaLogoName width={90} />
                    </Inline>
                    <Divider />
                </Stack>
            </Card>
        </Page>
    );
};
